import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TRANSLATIONS } from './translations';

const I18nContext = createContext();

const STORAGE_KEY = 'qoldo_lang';
const DEFAULT_LANG = 'nl';

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    // Initialize from localStorage or default
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && (stored === 'nl' || stored === 'en')) {
        return stored;
      }
    }
    return DEFAULT_LANG;
  });

  // Persist language choice
  const setLang = useCallback((newLang) => {
    if (newLang === 'nl' || newLang === 'en') {
      setLangState(newLang);
      localStorage.setItem(STORAGE_KEY, newLang);
    }
  }, []);

  // Translation function
  const t = useCallback((key, replacements = {}) => {
    const translations = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANG];
    let text = translations[key] || TRANSLATIONS[DEFAULT_LANG][key] || key;
    
    // Handle replacements like {courseName}
    Object.entries(replacements).forEach(([k, v]) => {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
    });
    
    return text;
  }, [lang]);

  // Mode-aware translation: tries key_{experienceType}, falls back to key (training)
  const tm = useCallback((key, experienceType, replacements = {}) => {
    if (experienceType && experienceType !== 'training') {
      const modeKey = `${key}_${experienceType}`;
      const translations = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANG];
      if (translations[modeKey] || TRANSLATIONS[DEFAULT_LANG][modeKey]) {
        return t(modeKey, replacements);
      }
    }
    return t(key, replacements);
  }, [lang, t]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, tm }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
}

export { I18nContext };
